import { CSSProperties, useCallback, useEffect, useRef, useState } from "react";
import { Input, InputRef } from "antd";
import { useTranslation } from "react-i18next";

import useSearchHotkey from "@/v1/components/hooks/useSearchHotkey";
import { useCustomerSearch } from "./useCustomerSearch";

const { Search } = Input;

/**
 * The one customer search box (SMT-1396). Accepts email, Public ID, Account ID
 * and tax number, and hands the view the customer's real email (or
 * `undefined`), so the view's own storage and API calls stay as they are.
 *
 * `value` is the currently selected email. The box follows it when it changes
 * from outside (URL seed, Load all, row expand), except right after a search
 * that matched nobody unique: the typed text then stays so it can be corrected.
 */
const CustomerSearchInput = ({
  value,
  onSelect,
  id = "customer-search",
  style,
  disabled,
}: {
  value?: string | null;
  onSelect: (email: string | undefined) => void;
  id?: string;
  style?: CSSProperties;
  disabled?: boolean;
}) => {
  const { t } = useTranslation();
  const [text, setText] = useState(value || "");
  const keepTypedText = useRef(false);
  const searchRef = useRef<InputRef>(null);
  useSearchHotkey(searchRef);

  // Set before `onSelect`, because the parent may re-render (and run the
  // effect below) synchronously inside it. Only when `value` is set: otherwise
  // it does not change, the effect never runs and the flag would linger.
  const handleSelect = useCallback(
    (email: string | undefined) => {
      keepTypedText.current = !email && Boolean(value) && Boolean(text.trim());
      // Show who was found, also when it is the customer already selected.
      if (email) setText(email);
      onSelect(email);
    },
    [onSelect, text, value]
  );

  const { search } = useCustomerSearch(handleSelect);

  useEffect(() => {
    if (keepTypedText.current) {
      keepTypedText.current = false;
      return;
    }
    setText(value || "");
  }, [value]);

  return (
    <Search
      ref={searchRef}
      id={id}
      data-test="customerSearch"
      value={text}
      onChange={(event) => setText(event.target.value)}
      onSearch={(input: string) => {
        search(input);
      }}
      // Enter searches; inside a <form> it must not also submit the form
      // with the email from before the search resolved.
      onKeyPress={(event) => {
        if (event.key === "Enter") {
          event.preventDefault();
        }
      }}
      placeholder={t("customerSearch.placeholder") ?? undefined}
      allowClear
      enterButton={t("customerSearch.filter")}
      // No `loading` prop: antd's Search ignores Enter while loading, which
      // would silently drop a second search typed during the first. The hook
      // keeps only the newest result instead.
      disabled={disabled}
      style={style}
    />
  );
};

export default CustomerSearchInput;
